/**
 * ThemedSelect — a drop-in replacement for the native `<select>`.
 *
 * The native option list is drawn by the OS and ignores the theme. This keeps
 * the exact `<select>` contract (value / defaultValue / onChange(event) / name /
 * required / disabled / ref / `<option>` + `<optgroup>` children) by rendering
 * the real `<select>` visually hidden underneath, and drawing a themed trigger +
 * rounded menu on top. Picking an item sets the hidden select's value and fires
 * a real `change` event, so React `onChange`, form submission, `FormData`,
 * refs, `<label htmlFor>` and tests using `selectOptions` all keep working.
 *
 * The menu renders inline (not portalled) so "click outside" handlers of any
 * popover the select sits in still see clicks on the menu as inside.
 */
import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { clsx } from 'clsx';
import { Check, ChevronDown } from 'lucide-react';

type Item =
  | { kind: 'option'; value: string; label: string; disabled: boolean }
  | { kind: 'group'; label: string };

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
  return '';
}

function collectItems(children: ReactNode, out: Item[] = [], groupDisabled = false): Item[] {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const props = child.props as Record<string, any>;
    if (child.type === Fragment) {
      collectItems(props.children, out, groupDisabled);
    } else if (child.type === 'optgroup') {
      out.push({ kind: 'group', label: String(props.label ?? '') });
      collectItems(props.children, out, groupDisabled || Boolean(props.disabled));
    } else if (child.type === 'option') {
      const label = textOf(props.children);
      out.push({
        kind: 'option',
        value: props.value !== undefined ? String(props.value) : label,
        label,
        disabled: groupDisabled || Boolean(props.disabled),
      });
    }
  });
  return out;
}

// Classes that size/position the control belong on the wrapper, not the button.
const LAYOUT_CLASS =
  /^(-?m[trblxy]?-|w-|min-w-|max-w-|flex-|grow|shrink|basis-|self-|order-|col-|row-|justify-self-|place-self-)/;

function splitClasses(className?: string): { wrapper: string; control: string } {
  const wrapper: string[] = [];
  const control: string[] = [];
  for (const cls of (className || '').split(/\s+/).filter(Boolean)) {
    const base = cls.slice(cls.lastIndexOf(':') + 1).replace(/^!/, '');
    (LAYOUT_CLASS.test(base) ? wrapper : control).push(cls);
  }
  return { wrapper: wrapper.join(' '), control: control.join(' ') };
}

function setNativeValue(select: HTMLSelectElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
  if (setter) setter.call(select, value);
  else select.value = value;
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

export type ThemedSelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const ThemedSelect = forwardRef<HTMLSelectElement, ThemedSelectProps>(function ThemedSelect(
  { className, children, onChange, disabled, style, ...rest },
  ref,
) {
  const selectRef = useRef<HTMLSelectElement>(null);
  useImperativeHandle(ref, () => selectRef.current as HTMLSelectElement);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  // Where the open list sits, in viewport coordinates. It is `position: fixed`
  // rather than absolute so a scrolling dialog or an `overflow-hidden` card
  // cannot clip it — which is what hid half the options and covered the
  // footer buttons when a select was the last field in a dialog.
  const [menuPos, setMenuPos] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    minWidth: number;
    maxHeight: number;
  } | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Mirrors the hidden select's value (covers uncontrolled selects).
  const [domValue, setDomValue] = useState<string | undefined>(undefined);

  const items = collectItems(children);
  const options = items.filter((i): i is Extract<Item, { kind: 'option' }> => i.kind === 'option');

  const controlled = rest.value !== undefined && !Array.isArray(rest.value);
  const currentValue = controlled ? String(rest.value) : domValue;

  useLayoutEffect(() => {
    if (selectRef.current) setDomValue(selectRef.current.value);
  });

  const selected =
    options.find((o) => o.value === currentValue) ?? (currentValue === undefined ? options[0] : undefined);
  const isPlaceholder = selected ? selected.value === '' : !currentValue;

  const close = useCallback((refocus = false) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const openMenu = () => {
    if (disabled) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const below = window.innerHeight - rect.bottom;
      // Open downward whenever about four options fit, and let the list scroll.
      // Flipping up at the first sign of a tight fit put the list over the
      // field's own label and the fields above it; it now flips only when
      // there is genuinely no room below.
      const up = below < 170 && rect.top > below;
      setMenuPos({
        left: rect.left,
        top: up ? undefined : rect.bottom + 6,
        bottom: up ? window.innerHeight - rect.top + 6 : undefined,
        minWidth: rect.width,
        // Never taller than the room it has, so every option stays reachable.
        maxHeight: Math.max(120, Math.min(256, (up ? rect.top : below) - 14)),
      });
    }
    const idx = items.findIndex((i) => i.kind === 'option' && i.value === selected?.value);
    setActiveIndex(idx);
    setOpen(true);
  };

  const choose = (value: string) => {
    const select = selectRef.current;
    close(true);
    if (!select || value === currentValue) return;
    setNativeValue(select, value);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // A fixed list does not travel with its trigger, so a scroll or resize
  // underneath it closes it (scrolling the list itself does not).
  useEffect(() => {
    if (!open) return;
    const onScroll = (e: Event) => {
      if (listRef.current && e.target instanceof Node && listRef.current.contains(e.target)) return;
      close();
    };
    const onResize = () => close();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  // `fixed` is measured from the viewport unless an ancestor has a transform
  // or filter, in which case it is measured from that ancestor. Compare where
  // the list landed with where it was meant to be and nudge it; the same pass
  // keeps it from running off the right edge.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!open || !el || !menuPos) return;
    el.style.transform = '';
    const r = el.getBoundingClientRect();
    const wantLeft = Math.max(8, Math.min(menuPos.left, window.innerWidth - r.width - 8));
    const wantTop = menuPos.top ?? window.innerHeight - (menuPos.bottom ?? 0) - r.height;
    const dx = wantLeft - r.left;
    const dy = wantTop - r.top;
    if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) el.style.transform = `translate(${dx}px, ${dy}px)`;
  }, [open, menuPos]);

  // Keep the active item in view while arrowing.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  const move = (dir: 1 | -1) => {
    if (items.length === 0) return;
    let i = activeIndex;
    for (let n = 0; n < items.length; n++) {
      i = (i + dir + items.length) % items.length;
      const it = items[i];
      if (it.kind === 'option' && !it.disabled) {
        setActiveIndex(i);
        return;
      }
    }
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const it = items[activeIndex];
      if (it && it.kind === 'option' && !it.disabled) choose(it.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === 'Tab') {
      close();
    }
  };

  const { wrapper, control } = splitClasses(className);

  return (
    <div ref={wrapperRef} className={clsx('relative inline-block align-middle', wrapper)} style={style}>
      <select
        {...rest}
        ref={selectRef}
        disabled={disabled}
        onChange={(e) => {
          setDomValue(e.target.value);
          onChange?.(e);
        }}
        tabIndex={-1}
        className="sr-only"
      >
        {children}
      </select>

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={rest.title}
        className={clsx(
          // A bare <select> falls back to the house field style.
          control || 'input-field',
          '!flex w-full items-center justify-between gap-2 text-left !bg-none',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        )}
      >
        <span className={clsx('min-w-0 truncate', isPlaceholder && 'text-[var(--co-ink-4)]')}>
          {/* A saved value that is not one of the options (a field type or time
              zone the list does not offer) is shown as stored, not as a
              blank trigger that looks like nothing was ever chosen. */}
          {selected?.label ?? currentValue ?? ''}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={clsx(
            'h-4 w-4 flex-none text-[var(--co-ink-4)] transition-transform duration-[120ms]',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="fixed z-[60] w-max max-w-[min(360px,90vw)] overflow-y-auto rounded-[12px] border border-[var(--co-line)] bg-[var(--co-raised)] p-1 text-left shadow-[var(--co-shadow-pop)]"
          style={
            menuPos
              ? {
                  left: menuPos.left,
                  top: menuPos.top,
                  bottom: menuPos.bottom,
                  minWidth: menuPos.minWidth,
                  maxHeight: menuPos.maxHeight,
                }
              : undefined
          }
        >
          {items.length === 0 && (
            <li className="px-3 py-2 text-[13px] text-[var(--co-ink-4)]">No options</li>
          )}
          {items.map((it, index) =>
            it.kind === 'group' ? (
              <li
                key={`g-${index}`}
                role="presentation"
                className="px-3 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[.08em] text-[var(--co-ink-4)]"
              >
                {it.label}
              </li>
            ) : (
              <li
                key={`o-${index}-${it.value}`}
                role="option"
                aria-selected={it.value === selected?.value}
                aria-disabled={it.disabled || undefined}
                data-index={index}
                onMouseEnter={() => !it.disabled && setActiveIndex(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => !it.disabled && choose(it.value)}
                className={clsx(
                  'flex items-center justify-between gap-3 rounded-[8px] px-3 py-2 text-[13px] transition-colors duration-[120ms]',
                  it.disabled
                    ? 'cursor-not-allowed text-[var(--co-ink-4)] opacity-60'
                    : 'cursor-pointer',
                  it.value === selected?.value
                    ? 'bg-[var(--co-brass-wash)] font-semibold text-[var(--co-brass-ink)]'
                    : index === activeIndex
                      ? 'bg-[var(--co-canvas)] text-[var(--co-ink)]'
                      : 'text-[var(--co-ink-2)]',
                )}
              >
                <span className="min-w-0 truncate">{it.label || ' '}</span>
                {it.value === selected?.value && <Check className="h-3.5 w-3.5 flex-none" />}
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
});

export default ThemedSelect;
